import React, { useState, useEffect } from 'react';
import api from '../../utils/api';

export default function AdminImport() {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [logs, setLogs] = useState([]);

  useEffect(() => { api.get('/import/logs').then(r => setLogs(r.data)).catch(() => {}); }, []);

  const handleImport = async () => {
    if (!file) return setError('Select a CSV file first');
    setImporting(true); setError(''); setResult(null);
    const data = new FormData();
    data.append('file', file);
    try {
      const r = await api.post('/import/csv', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(r.data);
      api.get('/import/logs').then(r => setLogs(r.data)).catch(() => {});
    } catch (e) { setError(e.response?.data?.error || 'Import failed'); }
    setImporting(false);
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1a2744', marginBottom: 2 }}>Bravo CSV Import</h1>
        <p style={{ color: '#9a9a9a', fontSize: 13 }}>Import inventory from your Bravo POS CSV export. Existing items (matched by SKU) will be updated; new items will be created.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
        {/* Upload */}
        <div style={{ background: '#fff', borderRadius: 14, padding: 28, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <h3 style={{ fontWeight: 700, color: '#1a2744', marginBottom: 16 }}>Upload CSV</h3>

          {error && <div className="alert alert-error">{error}</div>}
          {result && (
            <div className="alert alert-success" style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>Import Complete</div>
              <div style={{ fontSize: 13 }}>✅ {result.imported} new items added</div>
              <div style={{ fontSize: 13 }}>🔄 {result.updated} existing items updated</div>
              <div style={{ fontSize: 13 }}>⏭️ {result.skipped} rows skipped (no name/SKU)</div>
              <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4 }}>Total rows processed: {result.total}</div>
            </div>
          )}

          <div style={{ border: '2px dashed #e8e8e8', borderRadius: 10, padding: '32px 20px', textAlign: 'center', marginBottom: 16, background: file ? 'rgba(201,162,39,0.05)' : '#fafafa', borderColor: file ? '#c9a227' : '#e8e8e8', transition: 'all 0.15s' }}>
            {file ? (
              <div>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📄</div>
                <div style={{ fontWeight: 700, color: '#1a2744', marginBottom: 4 }}>{file.name}</div>
                <div style={{ fontSize: 12, color: '#9a9a9a' }}>{(file.size / 1024).toFixed(1)} KB</div>
                <button onClick={() => setFile(null)} style={{ marginTop: 10, fontSize: 12, color: '#c62828', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Remove</button>
              </div>
            ) : (
              <label style={{ cursor: 'pointer' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📥</div>
                <div style={{ fontWeight: 600, color: '#1a2744', marginBottom: 4 }}>Drop your Bravo CSV here</div>
                <div style={{ fontSize: 12, color: '#9a9a9a', marginBottom: 12 }}>or click to browse</div>
                <span className="btn btn-outline btn-sm">Choose File</span>
                <input type="file" accept=".csv" style={{ display: 'none' }} onChange={e => setFile(e.target.files[0])} />
              </label>
            )}
          </div>

          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={handleImport} disabled={!file || importing}>
            {importing ? '⏳ Importing...' : '📥 Import CSV'}
          </button>
        </div>

        {/* Instructions + field map */}
        <div>
          <div style={{ background: '#fff', borderRadius: 14, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 16 }}>
            <h3 style={{ fontWeight: 700, color: '#1a2744', marginBottom: 14 }}>How to Export from Bravo</h3>
            {[
              'Open Bravo POS and go to Reports',
              'Select "Inventory" or "Current Inventory" report',
              'Set date range to current',
              'Export as CSV (.csv)',
              'Upload the file here',
            ].map((step, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                <div style={{ width: 22, height: 22, background: '#c9a227', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 900, color: '#1a2744', flexShrink: 0 }}>{i + 1}</div>
                <span style={{ fontSize: 13, color: '#444', alignSelf: 'center' }}>{step}</span>
              </div>
            ))}
          </div>
          <div style={{ background: '#fff', borderRadius: 14, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <h3 style={{ fontWeight: 700, color: '#1a2744', marginBottom: 12 }}>Recognized Column Names</h3>
            <p style={{ fontSize: 12, color: '#9a9a9a', marginBottom: 12 }}>The importer auto-detects these column headers (case-insensitive):</p>
            {[['Title/Name', 'Description, Name, Title, Item Description'],['SKU', 'SKU, Item Number, Stock Number, Barcode'],['Price', 'Price, Sale Price, Asking Price'],['Quantity', 'Qty, Quantity, Stock, On Hand'],['Condition', 'Condition, Grade'],['Make', 'Make, Brand, Manufacturer'],['Model', 'Model, Model Number'],].map(([field, variants]) => (
              <div key={field} style={{ display: 'flex', gap: 10, fontSize: 12, marginBottom: 6, padding: '6px 0', borderBottom: '1px solid #f4f4f4' }}>
                <span style={{ fontWeight: 700, color: '#1a2744', width: 80, flexShrink: 0 }}>{field}</span>
                <span style={{ color: '#666' }}>{variants}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Import history */}
      {logs.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 14, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginTop: 20 }}>
          <h3 style={{ fontWeight: 700, color: '#1a2744', marginBottom: 14 }}>Recent Imports</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: '2px solid #e8e8e8' }}>{['Date','File','Added','Updated','Skipped','By'].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, fontWeight: 700, color: '#9a9a9a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>)}</tr></thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.id} style={{ borderBottom: '1px solid #f4f4f4' }}>
                  <td style={{ padding: '10px 12px', fontSize: 12, color: '#666' }}>{new Date(log.created_at).toLocaleDateString()}</td>
                  <td style={{ padding: '10px 12px', fontSize: 12, color: '#1a2744', fontWeight: 600 }}>{log.filename}</td>
                  <td style={{ padding: '10px 12px', fontSize: 12, color: '#2e7d32', fontWeight: 700 }}>{log.records_imported}</td>
                  <td style={{ padding: '10px 12px', fontSize: 12, color: '#1976d2', fontWeight: 700 }}>{log.records_total - log.records_imported - log.records_skipped || 0}</td>
                  <td style={{ padding: '10px 12px', fontSize: 12, color: '#9a9a9a' }}>{log.records_skipped}</td>
                  <td style={{ padding: '10px 12px', fontSize: 12, color: '#666' }}>{log.imported_by_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
